import { useState } from 'react';

import { QUIET_ACTION } from '@/lib/ui-tokens';

/**
 * MediaStudioOriginalPromptCard — pinned context card showing the prompt
 * the asset was originally generated from. Long prompts (over the
 * line-clamp threshold) collapse with a "Show more" toggle to keep the
 * rail's vertical rhythm intact while preserving full text + selectability
 * for copy. Matches the editorial expand pattern used across project detail surfaces.
 *
 * Per design-system: `text-detail` editorial body + small-caps label,
 * `bg-muted/40` card surface, `rounded-lg`. NOT skill-purple — this is a
 * committed/durable surface, not an AI-Temp suggestion.
 */
const COLLAPSED_LINE_CLAMP = 3;
const TRUNCATE_THRESHOLD_CHARS = 160;

interface MediaStudioOriginalPromptCardProps {
  prompt: string;
}

export function MediaStudioOriginalPromptCard({ prompt }: MediaStudioOriginalPromptCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isLong = prompt.length > TRUNCATE_THRESHOLD_CHARS;

  return (
    <div className="rounded-lg border border-border/60 bg-muted/40 px-3.5 py-2.5">
      <p className="text-micro font-medium uppercase tracking-wide text-muted-foreground/80 mb-1">
        Original prompt
      </p>
      <p
        className="text-detail leading-relaxed text-foreground/85"
        style={
          isLong && !expanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: COLLAPSED_LINE_CLAMP,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {prompt}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`${QUIET_ACTION} mt-1.5 text-detail font-medium text-muted-foreground/70`}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
